import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@bcc/components/ui/tooltip";
import { ImageOff, Upload } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { setProductThumbnailTestId } from "./product-thumbnail.utils";

interface ProductThumbnailProps {
  imageUrl?: string;
  productName: string;
  onClick: () => void;
  allowUpload?: boolean;
  onUpload?: () => void;
  testId?: string;
}

export function ProductThumbnail({
  imageUrl,
  productName,
  onClick,
  allowUpload = false,
  onUpload,
  testId,
}: ProductThumbnailProps) {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);
  const hasImage = imageUrl && !imageError;

  if (!hasImage) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              data-testid={testId ?? setProductThumbnailTestId("placeholder")}
              className="h-12 w-12 rounded-lg border-2 border-dashed border-border bg-muted flex items-center justify-center cursor-pointer hover:border-accent transition-colors"
              onClick={allowUpload && onUpload ? onUpload : undefined}
            >
              {allowUpload ? (
                <Upload className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ImageOff className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {allowUpload
                ? t("products.thumbnail.uploadImage")
                : t("products.thumbnail.noImageAvailable")}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            data-testid={testId ?? setProductThumbnailTestId("button")}
            className="h-12 w-12 rounded-lg border border-border-subtle overflow-hidden hover:border-accent hover:shadow-md transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
            onClick={onClick}
            aria-label={t("products.thumbnail.viewImage", {
              name: productName,
            })}
          >
            <img
              src={imageUrl || "/placeholder.svg"}
              alt={`${productName} thumbnail`}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={() => setImageError(true)}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t("products.thumbnail.clickToEnlarge")}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
